import { useEffect, useReducer, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getQueuedCount, setupOfflineSync, isOnline } from '../lib/offlineCheckin';
import { useAuth } from '../hooks/useAuth';
import { Smartphone, Check, X } from 'lucide-react';

export default function OfflineSyncStatus() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // #3733: the badge counts only the signed-in member's queued check-ins. The
  // count is read at render so it follows the current user; the poll and the
  // sync callback just ask for a re-render.
  const [, refreshQueueCount] = useReducer((n: number) => n + 1, 0);
  const queueCount = getQueuedCount(user?.id);
  const [online, setOnline] = useState(isOnline());
  const [lastSyncResult, setLastSyncResult] = useState<{
    success: number;
    failed: number;
  } | null>(null);

  useEffect(() => {

    // Setup online/offline listeners
    const handleOnline = () => {
      setOnline(true);
    };

    const handleOffline = () => {
      setOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Setup offline sync with callback
    const cleanup = setupOfflineSync((result) => {
      // #3598: a flushed check-in changes check-in-derived event state, so
      // refresh the cached event queries for the events that uploaded. The
      // queue posts to the UUID, but a detail reached from a shared link is
      // cached under its short code, so match on the cached event's id too.
      // Scoped to those events (not the whole ['event'] prefix) so an
      // unrelated active event query, e.g. the duplicate-event form's source
      // (EventCreatePage swaps the form for a loading screen while it
      // fetches), is not refetched by a background flush.
      if (result.synced.length > 0) {
        const eventIds = new Set(result.synced.map((item) => item.eventId));
        queryClient.invalidateQueries({
          predicate: (query) => {
            const [root, keyId] = query.queryKey;
            if (root !== 'event') return false;
            if (typeof keyId === 'string' && eventIds.has(keyId)) return true;
            const cachedId = (query.state.data as { id?: unknown } | undefined)?.id;
            return typeof cachedId === 'string' && eventIds.has(cachedId);
          },
        });
      }
      setLastSyncResult(result);
      refreshQueueCount();
      
      // Clear sync result after 5 seconds
      setTimeout(() => {
        setLastSyncResult(null);
      }, 5000);
    });

    // Poll for queue count changes (in case items are added)
    const interval = setInterval(() => {
      refreshQueueCount();
    }, 2000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      cleanup();
      clearInterval(interval);
    };
  }, [queryClient]);

  // Don't show anything if online and no queued items
  if (online && queueCount === 0 && !lastSyncResult) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Offline Indicator with Queue Count */}
      {!online && queueCount > 0 && (
        <div className="bg-oro-warning/20 border border-oro-warning/40 rounded-[var(--radius-md)] px-4 py-3 shadow-lg backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <Smartphone className="w-6 h-6 text-oro-warning" />
            <div>
              <p className="text-sm font-semibold text-oro-warning">Offline Mode</p>
              <p className="text-xs text-[var(--color-text-secondary)]">
                {queueCount} check-in{queueCount !== 1 ? 's' : ''} queued
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sync Success Notification */}
      {lastSyncResult && lastSyncResult.success > 0 && (
        <div className="bg-oro-success/20 border border-oro-success/40 rounded-[var(--radius-md)] px-4 py-3 shadow-lg backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <Check className="w-6 h-6 text-oro-success" />
            <div>
              <p className="text-sm font-semibold text-oro-success">Synced!</p>
              <p className="text-xs text-[var(--color-text-secondary)]">
                {lastSyncResult.success} check-in{lastSyncResult.success !== 1 ? 's' : ''} uploaded
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sync Failure Notification */}
      {lastSyncResult && lastSyncResult.failed > 0 && (
        <div className="bg-oro-error/20 border border-oro-error/40 rounded-[var(--radius-md)] px-4 py-3 shadow-lg backdrop-blur-sm mt-2">
          <div className="flex items-center gap-3">
            <X className="w-6 h-6 text-oro-error" />
            <div>
              <p className="text-sm font-semibold text-oro-error">Sync Failed</p>
              <p className="text-xs text-[var(--color-text-secondary)]">
                {lastSyncResult.failed} check-in{lastSyncResult.failed !== 1 ? 's' : ''} couldn't sync
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
